import { useAppContext } from '@ifixit/app/hooks';
import { useAuthenticatedUser, type User } from '@ifixit/auth-sdk';
import { SentryError } from '@ifixit/sentry';
import {
   type AttributeInput,
   type CartInput,
   type CartWarning,
   type CountryCode,
   type LanguageCode,
   type StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useCallback } from 'react';
import { useGetCustomerAccessToken } from '../use-get-customer-access-token';
import { getSyncedAttributes } from '../../helpers/attributes';
import { clearStoredCustomerAccessToken, updateCartId } from '../../helpers/storage';

type UseCreateCartProps = {
   lines?: {
      merchandiseId: string;
      quantity: number;
   }[];
   country: CountryCode;
   language: LanguageCode;
};
export function useCreateCart() {
   const { isOnMobileApp } = useAppContext();
   const userQueryResult = useAuthenticatedUser();
   const { client, storeCode } = useShopifyStorefrontClient();
   const getCustomerAccessToken = useGetCustomerAccessToken();
   return useCallback(
      async ({ country, lines, language }: UseCreateCartProps) => {
         const user = userQueryResult.data ?? null;
         const { attributes } = getSyncedAttributes({ cart: null, isOnMobileApp });
         let cartCreate = await createCart({
            attributes,
            client,
            country,
            getCustomerAccessToken,
            lines,
            language,
            user,
         });
         if (cartCreate?.userErrors.some(error => error.message === 'Customer is invalid')) {
            // If the customer access token is invalid or expired, clear it from
            // storage and try again. Type assertion: we know the user exists.
            clearStoredCustomerAccessToken({ storeCode, user: user! });
            cartCreate = await createCart({
               attributes,
               client,
               country,
               getCustomerAccessToken,
               lines,
               language,
               user,
            });
         }

         const userErrors = cartCreate?.userErrors ?? [];
         const validationErrors: string[] = [];
         const warnings: CartWarning[] = [];
         const cart = cartCreate?.cart;
         const cartId = cart?.id;

         if (!cart || !cartId) {
            throw new SentryError('Failed to create cart', { extra: { lines, userErrors } });
         }

         updateCartId({ cartId, storeCode, userId: user?.id ?? null });
         return { cart, userErrors, validationErrors, warnings };
      },
      [client, getCustomerAccessToken, storeCode, userQueryResult]
   );
}

async function createCart({
   attributes,
   client,
   country,
   getCustomerAccessToken,
   lines,
   language,
   user,
}: {
   attributes: AttributeInput[];
   client: StorefrontClient;
   country: CountryCode;
   getCustomerAccessToken: ReturnType<typeof useGetCustomerAccessToken>;
   lines: UseCreateCartProps['lines'];
   language: LanguageCode;
   user: User | null;
}) {
   const customerAccessToken = user ? await getCustomerAccessToken(user) : null;
   const input: CartInput = {
      attributes,
      buyerIdentity: { customerAccessToken: customerAccessToken?.accessToken },
      lines,
   };
   const { cartCreate } = await client.createCart({ input, country, language });
   return cartCreate;
}
